import React, { useContext, useEffect } from 'react';

import { TrackEventContext } from 'common/containers/EventContainer';
import MercuryLandingHelmet from 'common/helmets/MercuryLandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';
import Mercury1 from 'img/landing/mercury-1.webp';
import MercuryWordmark from 'img/landing/mercury-wordmark.webp';

import 'css/components/www/_LandingCustomersPage.scss';

const MercuryLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed Mercury Case Study');
  }, [trackEvent]);

  return (
    <div className="landingMercury landingCustomersPage">
      <MercuryLandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="How Mercury found a drastic improvement in efficiency and productivity" />
          <div className="headerVideo">
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/QPguo5_UXBw"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen></iframe>
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              If you ask any entrepreneur if they like their bank, you’ll quickly get a list of pain
              points. Nothing about the experience is modern or delightful. Banking can be
              challenging, confusing and complicated, especially for startups. There are a million
              things to manage already. That’s why Mercury was born—to enable founders and finance
              teams to spend less time thinking about&nbsp;banking.
            </p>
            <p>
              Mercury offers vibrant community programs that provide founders with the connections,
              advice, and resources to help them build the next great companies. While serving the
              startup world and building a community, Mercury heavily relies on user feedback.
              That’s where Canny comes&nbsp;in.
            </p>
            <h2 className="sectionTitle">Challenge</h2>
            <p>
              Before adopting Canny, Ida Ström, Mercury’s senior product designer, was spending too
              much tracking feature requests. They would get requests in Slack and email, then tried
              using Notion and Linear to stay&nbsp;organized.
            </p>
            <p className="quote">
              &ldquo;Keeping track of all of them was getting messy. Now we use Canny as one source
              of truth. And it’s much easier to select which things we need to build and focus
              on&nbsp;them.&rdquo;
            </p>
            <p>
              Sometimes, adding another tool to your tech stack doesn’t really feel like a solution
              though.
            </p>
            <p className="quote">
              &ldquo;We already use so many different tools, so adopting a new one can come with its
              challenges. But Canny is so easy to use and so straightforward that switching to it
              felt&nbsp;natural.&rdquo;
            </p>
            <h2 className="sectionTitle">Solution</h2>
            <p>Canny offered a solution for every issue that Mercury was&nbsp;experiencing:</p>
            <ol>
              <li>
                <p className="bold">Prioritization</p>
                <p>
                  The voting feature helps them see which issues are more pressing than others and
                  prioritize&nbsp;accordingly.
                </p>
              </li>
              <li>
                <p className="bold">Workload management</p>
                <p>
                  They use the boards feature to make sure that feedback is triaged to the
                  right&nbsp;team.
                </p>
              </li>
              <li>
                <p className="bold">Getting clarity</p>
                <p>
                  They use comments to reach out and ask people for more details or context around
                  the issues before they work on a solution.
                </p>
              </li>
            </ol>
            <p>
              Change doesn’t happen overnight though. Every process takes some time, and the team at
              Mercury was no different. Requests were still going into Slack for a couple months but
              then they people got used to&nbsp;it. However, according to Ida, this change was
              worth&nbsp;it.
            </p>
            <p className="quote">
              &ldquo;We actually saw an increase in user feedback because our users saw that we
              really do care about their feedback and invest in&nbsp;it.&rdquo;
            </p>
            <p>
              With Canny, the Mercury team does weekly Canny syncs where requests are prioritized
              and added to the roadmap.
            </p>
            <p>
              Internal teams reap the benefits of Canny as well. When new features are launched,
              they're celebrated.
            </p>
            <p className="quote">
              &ldquo;We always mention if that was a request that initially came from Canny. We give
              a shout out to the person who submitted it and use a special Canny emoji in Slack.
              This shows to our users that their requests matter and makes them
              feel&nbsp;special.&rdquo;
            </p>
            <LazyLoadedImage className="mercury1" src={Mercury1} />
            <p>
              The Mercury team also streamlines their workflows with Canny integrations like Slack.
              It allows them to be more efficient because they don't need to switch between apps or
              browser tabs.
            </p>
            <h2 className="sectionTitle">Results</h2>
            <p>
              Mercury’s team saw a drastic improvement in efficiency and productivity thanks
              to&nbsp;Canny.
            </p>
            <p className="quote">
              &ldquo;We use Canny as a way to distribute product thinking across the team. Now that
              everything is in one place, it’s so much easier and more&nbsp;efficient.&rdquo;
            </p>
            <p>
              They can see clearly that there are many requests that initially came from Canny and
              became shipped products, changes or&nbsp;features.
            </p>
            <p>
              Overall, Ida truly believes in the importance of getting feedback. And Canny is there
              to help her and her team to manage it in the best way possible.
            </p>
            <p className="quote">
              &ldquo;It’s very important to get feedback. Whether you use feedback for your external
              customers or internal teams, Canny builds a bridge for you. And that’s extremely
              important for building high quality&nbsp;products.&rdquo;
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage
              alt="Mercury Wordmark"
              className="mercuryWordmark"
              src={MercuryWordmark}
            />
            Mercury is banking* designed for the unique needs of startups - at any size or stage.
            With an intuitive product experience, founders can access free checking and savings
            accounts, debit and credit cards, domestic and international wire transfers, treasury,
            venture debt, and more. Mercury is trusted by companies like Sprig, Linear, Phantom, and
            Trust & Will.
            <span className="disclaimer">
              *Mercury is a financial technology company, not a bank. Banking services provided by
              Choice Financial Group and Evolve Bank & Trust, Members FDIC.
            </span>
            <a href="https://mercury.com/" rel="noopener" target="_blank">
              mercury.com
            </a>
            <div className="stat">
              <UppercaseHeader>Launched</UppercaseHeader>
              <p>2019</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Customers</UppercaseHeader>
              <p>100,000+ startups</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Location</UppercaseHeader>
              <p>San Francisco, CA</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Using Canny since</UppercaseHeader>
              <p>2021</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};

export default MercuryLanding;
